import {
    FormLayout,
    Form,
    TextField,
    Button,
    Select,
    LegacyStack,
    Frame,
    Checkbox,
    Text,
} from "@shopify/polaris";
import { useState, useCallback, useEffect } from 'react';
import Api from '../../helpers/api';
import {cloneDeep} from 'lodash';
import {CountrySelector, StateSelector} from "../Selectors";
import {filterShippingMethods, getFormError} from "../../helpers/utils";

const CustomerForm = props => {

    const [loading, setLoading] = useState(false);
    const [allShippingMethods, setAllShippingMethods] = useState({});
    const [shippingMethods, setShippingMethods] = useState([]);

    useEffect(() => {
        Api.getShippingMethods().then(results => {
            setAllShippingMethods(results.data);
        })
    }, [])

    const emptyCustomer = () => {
        return {
            'status': 'approved',  // Internal customers are just approved.
            'first_name': '',
            'last_name': '',
            'email': '',
            'shipping_method': null,
            'phone': '',
            'notes': '',
            'is_on_hold': false,
            'sms_consent': false,
            'email_consent': false,
            'shopify_company_location_id': '',

            location: {
                company: '',
                address1: '',
                address2: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
            },

            sync_hash: null,
        }
    };

    const existingCustomer = () => {
        return {
            ...props.customer,
            ...props.customer.additional_data,
            shipping_method: props.customer?.shipping_method ? {
                name: props.customer.shipping_method?.name ?? '',
                price: props.customer.shipping_method?.price ?? '0',
                key: props.customer.shipping_method?.shopify_rate_id ?? props.customer.shipping_method?.key //fallback for old values
            } : null,
            id: props.customer.id,
            status: props.customer.status,
            sync_hash: props.customer.sync_hash,
        }
    }

    const [toastActive, setToastActive] = useState(false);
    const [form, setForm] = useState((props.customer && props.updating)
        ? existingCustomer()
        : emptyCustomer() );

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const address = props.updating
            ? form.default_location?.shipping
            : form.location;

        if (allShippingMethods && (allShippingMethods.data || allShippingMethods.custom))
            setShippingMethods(filterShippingMethods(address, allShippingMethods));
    }, [allShippingMethods, form]);

    const handleInputChange = useCallback((value, id) => {
        const payload = {
            [id]: value,
        };

        setForm({
            ...form,
            ...payload,
        });
    }, [form]);

    const handleAddressChange = useCallback((value, id) => {
        let keys = id.split('.');
        let clonedForm = cloneDeep(form);
        clonedForm[keys[0]][keys[1]] = value;
        setForm(clonedForm);
    }, [form]);

    const handleShippingMethodChange = useCallback((key) => {
        let clonedForm = cloneDeep(form);

        if (!key || key.length == 0) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        clonedForm.shipping_method = {
            name: method.name,
            price: method.price,
            key: method.key,
        }
        setForm(clonedForm);
    }, [form, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        let clonedForm = cloneDeep(form);
        clonedForm.shipping_method[id] = value;
        setForm(clonedForm);
    }, [form]);

    const handleSubmit = () => {
        setLoading(true);
        setErrors({});

        Api.upsertCustomer(form)
            .then (({ data }) => {
                props.onCustomerCreated(data.data);

                setForm({
                    ...form,
                    ...data.data,
                });

                setLoading(false);
            })
            .catch ( error => {
                setLoading(false);

                if (error.response.status == 422) {
                    setErrors(error.response.data.errors);
                    if (error.response.data.errors.shopify_error) {
                        shopify.toast.show(error.response.data.errors.shopify_error[0], {isError: true});
                    } else {
                        shopify.toast.show("There has been an error processing the form.", {isError: true});
                        setErrors(error.response.data.errors);
                    }
                } else {
                    shopify.toast.show("There has been an error processing the form.", {isError: true});
                }

                setLoading(false);

                if (error.response.status != 422) {
                    throw error;
                }
            });
    }

    const showError = name => getFormError(errors, name);

    return (
        <Frame>
            <Form onSubmit={handleSubmit}>
            <FormLayout>
                <Text variant="headingSm" as="h3">Contact Information</Text>
                <FormLayout.Group>
                    <TextField label="First Name" value={form.first_name} onChange={handleInputChange} type="text" id="first_name" error={showError('first_name')} />
                    <TextField label="Last Name" value={form.last_name} onChange={handleInputChange} type="text" id="last_name" error={showError('last_name')} />
                </FormLayout.Group>
                <FormLayout.Group>
                    <TextField value={form.email} onChange={handleInputChange} label="Email" type="email" id="email" error={showError('email')} />
                    <TextField label="Phone" value={form.phone} onChange={handleInputChange} type="tel" id="phone" error={showError('phone')} />
                </FormLayout.Group>
                <FormLayout.Group>
                    <Checkbox label={'Email Marketing'} checked={form.email_consent} disabled={!(form.email?.length > 0)} id="email_consent" onChange={handleInputChange} />
                    <Checkbox label={'SMS Marketing'} checked={form.sms_consent} disabled={!(form.phone?.length > 0)} id="sms_consent" onChange={handleInputChange} />
                </FormLayout.Group>

                {props.creating && (
                    <>
                        <hr />
                        <Text variant="headingMd" as="h2">Location</Text>
                        <div style={{marginLeft: '-1%'}}>
                            <FormLayout.Group >
                                <LegacyStack vertical>
                                    <TextField label="Company" value={form.location?.company} onChange={handleAddressChange} type="text" id="location.company" error={showError('location.company')} />
                                    <TextField label="Address 1" value={form.location?.address1} onChange={handleAddressChange} type="text" id="location.address1" error={showError('location.address1')} />
                                    <TextField label="Address 2" value={form.location?.address2} onChange={handleAddressChange} type="text" id="location.address2" error={showError('location.address2')} />
                                    <TextField label="City" value={form.location?.city} onChange={handleAddressChange} type="text" id="location.city" error={showError('location.city')} />
                                    <TextField label="Postal Code" value={form.location?.zip_code} onChange={handleAddressChange} type="text" id="location.zip_code" error={showError('location.zip_code')} />
                                    <CountrySelector onChange={(val) => {handleAddressChange(val, "location.country")}}
                                                     value={form.location?.country} error={showError('location.country')} />
                                    <StateSelector onChange={(val) => {handleAddressChange(val, "location.state")}}
                                                   value={form.location?.state} error={showError('location.state')} selectedCountry={form.location?.country} />
                                </LegacyStack>
                            </FormLayout.Group>
                        </div>
                    </>
                )}

                <hr />
                <FormLayout.Group>
                    <TextField label="Notes" value={form.notes} onChange={handleInputChange} type="text" id="notes" error={showError('notes')} multiline={4} />
                </FormLayout.Group>
                <Button loading={loading} submit  variant="primary">Submit</Button>
            </FormLayout>
            </Form>
        </Frame>
    );
}

export default CustomerForm;
